'use client'
import { motion, useInView } from "motion/react";
import { TIndustriesConfig } from "@/app/config";
import { Brain, Headphones, Users, TrendingUp } from "lucide-react";
import React, { useRef } from "react";

const trendsIcons = [
  {
    
    icon: Brain,
    
  },
  {
    
    icon: Headphones,
  
  },
  {
    
    icon: Users,
    
  },
  {
    
    icon: TrendingUp,
   
  },
];

export default function TransformativeIndustryTrends({config}:{config:TIndustriesConfig['transformativeIndustryTrends']}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.15
      }
    }
  };

  // Custom hook for percentage counter animation
  const usePercentageAnimation = (targetPercentage: string , shouldAnimate : boolean) => {
    const [percentage, setPercentage] = React.useState(0);
    
    React.useEffect(() => {
      if (!shouldAnimate) return;
      
      const target = parseInt(targetPercentage);
      let current = 0;
      const increment = target / 60; // 60 frames for 1 second at 60fps
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setPercentage(target);
          clearInterval(timer);
        } else {
          setPercentage(Math.floor(current));
        }
      }, 33); // ~30fps for smooth animation
      
      return () => clearInterval(timer);
    }, [targetPercentage, shouldAnimate]);
    
    return percentage;
  };

  // ✅ Move hook calls outside of map - call hooks for each trend at component level
  const animatedPercentages = config.trends.map(trend => 
    // eslint-disable-next-line react-hooks/rules-of-hooks
    usePercentageAnimation(trend.percentage, isInView)
  );

  return (
    <motion.section 
      ref={ref}
      className="py-16 lg:py-20 bg-gray-50 relative overflow-hidden"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
    >
      {/* Animated background patterns */}
      <motion.div
        className="absolute inset-0 opacity-5"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.05, scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
      >
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-500 rounded-full blur-3xl" />
      </motion.div>

      {/* Floating geometric shapes */}
      {Array.from({ length: 8 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute bg-gradient-to-br from-blue-400 to-purple-400 opacity-10"
          style={{
            width: `${20 + Math.random() * 30}px`,
            height: `${20 + Math.random() * 30}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            borderRadius: Math.random() > 0.5 ? "50%" : "25%",
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            rotate: [0, 180, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 8 + Math.random() * 4,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut"
          }}
        />
      ))}

      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
            className="text-center mb-12"
            variants={{
              hidden: { 
                opacity: 0, 
                y: -40,
                scale: 0.9
              },
              visible: { 
                opacity: 1, 
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.8,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }
              }
            }}
          >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: -30, rotateX: -15 }}
            animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Trends Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          variants={containerVariants}
          style={{ perspective: "1000px" }}
        >
          {config.trends.map((trend, index) => { 
            const Icons = trendsIcons[index].icon;
            // ✅ Use the pre-calculated animated percentage instead of calling the hook here
            const animatedPercentage = animatedPercentages[index];
            
            return(
              <motion.div
                key={index}
                className={`${trend.bgColor} rounded-xl p-6 lg:p-8 border border-gray-200 relative overflow-hidden`}
                variants={{
                  hidden: { 
                    opacity: 0, 
                    y: 60,
                    scale: 0.85,
                    rotateX: -20
                  },
                  visible: (index: number) => ({
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    rotateX: 0,
                    transition: {
                      duration: 0.7,
                      delay: index * 0.2,
                      ease: "backOut",
                      type: "spring",
                      damping: 25
                    }
                  }),
                  hover: {
                    scale: 1.03,
                    y: -8,
                    rotateX: 5,
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
                    transition: {
                      duration: 0.3,
                      ease: "easeOut"
                    }
                  }
                }}
                custom={index}
                whileHover="hover"
                whileTap={{ scale: 0.98 }}
              >
                {/* Hover shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
                  whileHover={{
                    opacity: 0.1,
                    x: ["-100%", "100%"],
                    transition: { duration: 0.8, ease: "easeInOut" }
                  }}
                />

                {/* Header with Badge and Period */}
                <motion.div 
                  className="flex justify-between items-start mb-4"
                  variants={containerVariants}
                >
                  <motion.span 
                    className={`${trend.badgeColor} text-white text-xs font-semibold px-3 py-1 rounded-full`}
                    variants={ {
                      hidden: { opacity: 0, scale: 0.8, x: -20 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        x: 0,
                        transition: {
                          duration: 0.5,
                          delay: 0.3,
                          ease: "backOut"
                        }
                      },
                      hover: {
                        scale: 1.05,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                        transition: { duration: 0.2 }
                      }
                    }}
                    whileHover="hover"
                  >
                    {trend.badge}
                  </motion.span>
                  <motion.span 
                    className="text-gray-500 text-sm font-medium"
                    variants={{
                      hidden: { opacity: 0, x: 20 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.4,
                          delay: 0.4,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    {trend.period}
                  </motion.span>
                </motion.div>

                {/* Icon and Title */}
                <motion.div 
                  className="flex items-start gap-4 mb-4"
                  variants={containerVariants}
                >
                  <motion.div 
                    className={`w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
                    variants={ {
                      initial: {
                        scale: 1,
                        rotate: 0,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                      },
                      hover: {
                        scale: 1.15,
                        rotate: [0, -10, 10, 0],
                        boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                        transition: {
                          duration: 0.6,
                          ease: "easeInOut"
                        }
                      },
                      pulse: {
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          "0 2px 8px rgba(0, 0, 0, 0.1)",
                          "0 4px 12px rgba(0, 0, 0, 0.15)",
                          "0 2px 8px rgba(0, 0, 0, 0.1)"
                        ],
                        transition: {
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }
                      }
                    }}
                    initial="initial"
                    animate="pulse"
                    whileHover="hover"
                  >
                    <Icons className={`w-6 h-6 ${trend.iconColor}`} />
                  </motion.div>
                  <motion.h3 
                    className="text-xl lg:text-2xl font-semibold text-gray-900 leading-tight"
                    variants={ {
                      hidden: { opacity: 0, x: -30 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.6,
                          delay: 0.5,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    {trend.title}
                  </motion.h3>
                </motion.div>

                {/* Description */}
                <motion.p 
                  className="text-gray-600 text-sm lg:text-base leading-relaxed mb-6"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        delay: 0.6,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  {trend.description}
                </motion.p>

                {/* Adoption Progress */}
                <motion.div 
                  className="space-y-3"
                  variants={containerVariants}
                >
                  <motion.div 
                    className="flex justify-between items-center"
                    variants={{
                      hidden: { opacity: 0, scale: 0.8 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0.4,
                          delay: 0.7,
                          ease: "backOut"
                        }
                      }
                    }}
                  >
                    <span className="text-gray-700 font-medium text-sm">
                      {trend.adoption}
                    </span>
                    <motion.span 
                      className="text-gray-900 font-bold text-lg"
                      variants={{
                        hidden: { opacity: 0, scale: 0.8, y: -10 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            delay: 0.8,
                            ease: "backOut"
                          }
                        },
                        hover: {
                          scale: 1.1,
                          color: "#1f2937",
                          transition: { duration: 0.2 }
                        }
                      }}
                      whileHover="hover"
                    >
                      {isInView ? `${animatedPercentage}%` : trend.percentage}
                    </motion.span>
                  </motion.div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <motion.div 
                      className="bg-gray-800 h-2 rounded-full relative"
                      variants={{
                        hidden: { width: 0, opacity: 0 },
                        visible: (percentage:number) => ({
                          width: percentage,
                          opacity: 1,
                          transition: {
                            width: {
                              duration: 2,
                              delay: 0.8,
                              ease: "easeOut"
                            },
                            opacity: {
                              duration: 0.3,
                              delay: 0.7
                            }
                          }
                        })
                      }}
                      custom={trend.percentage}
                      initial="hidden"
                      animate={isInView ? "visible" : "hidden"}
                    >
                      {/* Progress bar glow effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                        animate={{
                          x: ["-100%", "100%"],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: 1 + index * 0.3,
                          ease: "easeInOut"
                        }}
                      />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Card-specific floating elements */}
                {Array.from({ length: 2 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-3 h-3 rounded-full opacity-20`}
                    style={{
                      background: trend.iconColor.includes('blue') ? '#3b82f6' :
                                trend.iconColor.includes('green') ? '#10b981' :
                                trend.iconColor.includes('purple') ? '#8b5cf6' :
                                '#f59e0b',
                      right: `${10 + i * 20}%`,
                      top: `${15 + i * 25}%`,
                    }}
                    animate={{
                      y: [0, -15, 0],
                      scale: [1, 1.3, 1],
                      opacity: [0.2, 0.5, 0.2],
                    }}
                    transition={{
                      duration: 3 + i,
                      repeat: Infinity,
                      delay: i * 0.8 + index * 0.2,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </motion.section>
  );
}